import type {Money} from "../../../../../lib/Money.ts";
import {
    AccountNature,
    BookingFilter,
    type BookingFilterCounts,
    type PanelBooking,
} from "../types.ts";
import type {PresentationSourceType} from "../../../../../generated/enums.ts";

const KIND_BY_FILTER = {
    [BookingFilter.Transactions]: "TRANSACTION",
    [BookingFilter.Bills]: "BILL",
    [BookingFilter.ManualBookings]: "MANUAL_BOOKING",
    [BookingFilter.FixedAssets]: "FIXED_ASSET",
    [BookingFilter.Matches]: "MATCH",
} as const satisfies Partial<Record<BookingFilter, PresentationSourceType>>;

const KIND_CHIP_SPECS: {
    filter: keyof typeof KIND_BY_FILTER;
    label: string;
    countKey: keyof BookingFilterCounts;
}[] = [
    {filter: BookingFilter.Transactions, label: "Transacties", countKey: "transactions"},
    {filter: BookingFilter.Bills, label: "Facturen", countKey: "bills"},
    {filter: BookingFilter.ManualBookings, label: "Memoriaal", countKey: "manualBookings"},
    {filter: BookingFilter.FixedAssets, label: "Vaste activa", countKey: "fixedAssets"},
    {filter: BookingFilter.Matches, label: "Betalingen", countKey: "matches"},
];

export function accountNature(signedNet: Money | null, bookings: PanelBooking[]): AccountNature {
    const cents = signedNet?.signed_amount ?? 0;
    if (cents > 0) return AccountNature.Credit;
    if (cents < 0) return AccountNature.Debit;
    const debit = bookings.reduce((sum, booking) => sum + booking.debit.signed_amount, 0);
    const credit = bookings.reduce((sum, booking) => sum + booking.credit.signed_amount, 0);
    return credit > debit ? AccountNature.Credit : AccountNature.Debit;
}

function isCounterNature(booking: PanelBooking, nature: AccountNature): boolean {
    return nature === AccountNature.Credit
        ? booking.debit.signed_amount > 0
        : booking.credit.signed_amount > 0;
}

export function matchesSearch(booking: PanelBooking, query: string): boolean {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const displayedDate = booking.date ? booking.date.split("-").reverse().join("-") : null;
    return [booking.title, booking.subtitle, booking.date, displayedDate]
        .filter((value): value is string => !!value)
        .some((value) => value.toLowerCase().includes(needle));
}

export function applyFilter(
    bookings: PanelBooking[],
    filter: BookingFilter,
    nature: AccountNature,
): PanelBooking[] {
    switch (filter) {
        case BookingFilter.All:
            return bookings;
        case BookingFilter.CounterNature:
            return bookings.filter((booking) => isCounterNature(booking, nature));
        case BookingFilter.Transactions:
        case BookingFilter.Bills:
        case BookingFilter.ManualBookings:
        case BookingFilter.FixedAssets:
        case BookingFilter.Matches:
            return bookings.filter((booking) => booking.kind === KIND_BY_FILTER[filter]);
        default: {
            const _exhaustive: never = filter;
            return _exhaustive;
        }
    }
}

function countByKind(bookings: PanelBooking[], kind: PresentationSourceType): number {
    return bookings.filter((booking) => booking.kind === kind).length;
}

export function filterCounts(bookings: PanelBooking[], nature: AccountNature): BookingFilterCounts {
    return {
        all: bookings.length,
        transactions: countByKind(bookings, "TRANSACTION"),
        bills: countByKind(bookings, "BILL"),
        manualBookings: countByKind(bookings, "MANUAL_BOOKING"),
        fixedAssets: countByKind(bookings, "FIXED_ASSET"),
        matches: countByKind(bookings, "MATCH"),
        counterNature: bookings.filter((booking) => isCounterNature(booking, nature)).length,
    };
}

export function visibleFilterChips(
    counts: BookingFilterCounts,
    nature: AccountNature,
): {filter: BookingFilter; label: string; count: number}[] {
    const chips: {filter: BookingFilter; label: string; count: number}[] = [
        {filter: BookingFilter.All, label: "Alle", count: counts.all},
    ];
    for (const spec of KIND_CHIP_SPECS) {
        const count = counts[spec.countKey];
        if (count > 0) {
            chips.push({filter: spec.filter, label: spec.label, count});
        }
    }
    if (counts.counterNature > 0) {
        chips.push({
            filter: BookingFilter.CounterNature,
            label: nature === AccountNature.Credit ? "Alleen debet" : "Alleen credit",
            count: counts.counterNature,
        });
    }
    return chips;
}
